// src/components/achievements/AchievementCard.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Achievement } from '../../services/achievementService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getBungieIconUrl } from '../../services/bungieIconService';

type Props = {
  achievement: Achievement;
  showProgress?: boolean;
};

const AchievementCard: React.FC<Props> = ({ achievement, showProgress = true }) => {
  const { t } = useLanguage();
  const [iconError, setIconError] = useState(false);
  
  const { id, name, description, is_completed, progress } = achievement;
  
  // Get Bungie icon URL through our proxy
  const iconUrl = !iconError && achievement.icon_url ? getBungieIconUrl(achievement.icon_url) : null;
  
  // FIX: Stelle sicher, dass wir immer gültige Werte für progress haben, auch wenn showProgress false ist
  // Dies vermeidet Layout-Shifts beim Wechsel zwischen authentifizierten und nicht-authentifizierten Ansichten
  const safeProgress = progress || { current: 0, total: 1, percentage: 0 };
  
  // FIX: Versichere, dass wir gültige Zahlen haben, auch wenn die API unvollständige Daten zurückgibt
  const progressCurrent = safeProgress.current ?? 0;
  const progressTotal = safeProgress.total ?? 1;
  // FIXED: Calculate percentage correctly based on completed status or current/total
  const progressPercentage = is_completed ? 100 : 
  (typeof safeProgress.exact_percentage === 'number' ? 
    Math.min(99, Math.round(safeProgress.exact_percentage)) : 
    (typeof safeProgress.percentage === 'number' ? 
      safeProgress.percentage : 
      (progressTotal > 0 ? Math.min(99, Math.round((progressCurrent * 100) / progressTotal)) : 0)));
  
  // Farbe basierend auf dem is_completed Status setzen
  const progressBarColor = is_completed ? 'bg-destiny-green' : 'bg-destiny-blue';
  
  // Eine Platzhalterfarbe basierend auf dem Achievement-Namen generieren
  // Das sorgt für eine konsistente Farbe pro Achievement für den Platzhalter
  const getPlaceholderColor = () => {
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 30%)`;
  };

  return (
    <Link
      to={`/achievements/${id}`}
      className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors"
    >
      <div className="p-4">
        <div className="flex items-start">
          {iconUrl ? (
            <img
              src={iconUrl}
              alt={name}
              className="w-12 h-12 mr-3 bg-gray-700 rounded-md"
              onError={(e) => {
                setIconError(true);
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.style.backgroundColor = getPlaceholderColor();
                target.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='; // Transparenter 1x1 Pixel
              }}
            />
          ) : (
            <div 
              className="w-12 h-12 mr-3 rounded-md flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: getPlaceholderColor() }}
            >
              {name.charAt(0)}
            </div>
          )}
          <div>
            <h3 className="text-white font-medium">{name}</h3>
            <p className="text-sm text-gray-400 mt-1 line-clamp-2">{description}</p>
          </div>
        </div>
        
        {/* FIX: Stelle sicher, dass wir konsistente Höhe haben, unabhängig vom showProgress-Wert */}
        <div className="mt-3 min-h-[30px]">
          {showProgress ? (
            <>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">
                  {is_completed
                    ? t('achievements.completed')
                    : safeProgress
                    ? `${t('achievements.progress')}: ${progressCurrent}/${progressTotal}`
                    : t('achievements.notStarted')}
                </span>
                <span className={is_completed ? 'text-destiny-green' : 'text-destiny-gold'}>
                  {progressPercentage}%
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-1.5">
                <div
                  className={`${progressBarColor} h-1.5 rounded-full`}
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </>
          ) : (
            // FIX: Für nicht-authentifizierte Nutzer einen minimalen Platzhalter einfügen,
            // der für konsistente Höhe sorgt
            <div className="h-4"></div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default AchievementCard;